import findIndex from 'lodash/findIndex';
import { firestoreAction } from 'vuexfire';
import * as firebase from 'firebase/app';

import { db } from '../../firebase';
import { moveByIndex, removeByIndex } from '../../utils/array';

function getDefaultApi(collection, field) {
  const nestedPath = Array.isArray(collection) && collection[1];
  const collectionRef = db.collection(nestedPath ? collection[0] : collection);
  const getParentRef = parentId =>
    nestedPath
      ? collectionRef
          .doc(parentId)
          .collection(nestedPath[0])
          .doc(nestedPath[1])
      : collectionRef.doc(parentId);

  async function attachInDB(parentId, childPath) {
    const parentRef = getParentRef(parentId);
    const childRef = db.doc(childPath);
    await parentRef.update({
      [field]: firebase.firestore.FieldValue.arrayUnion(childRef)
    });
    return childRef;
  }

  async function detachInDB(parentId, childPath) {
    const parentRef = getParentRef(parentId);
    const childRef = db.doc(childPath);
    await parentRef.update({
      [field]: firebase.firestore.FieldValue.arrayRemove(childRef)
    });
    return childRef;
  }

  async function moveInDB(data, from, to) {
    const parentRef = getParentRef(data.id);
    // TODO: this assumes that nested collections doc is put under selected
    // with docId as key. Make it more generic, or at least better design it.
    const nestedData = nestedPath ? data[nestedPath[1]] : data;
    const fromIndex = findIndex(nestedData[field], o => o.path === from);
    const toIndex = findIndex(nestedData[field], o => o.path === to);
    moveByIndex(nestedData[field], fromIndex, toIndex);
    await parentRef.update({
      [field]: nestedData[field]
    });
    return data;
  }

  return {
    attach: attachInDB,
    detach: detachInDB,
    move: moveInDB
  };
}

function shouldBindReferences(data, field) {
  return Array.isArray(data[field]);
}

/**
 * Use when a document has a field which is list of references to
 * other documents.
 *
 * **IMPORTANT NOTE**: Always use in conjunction with `selectable` plugin.
 *
 * @param {Object} relation Can have the following three options
 * @param {String} relation.collection String or Array that contains collection
 * location which contain the references.
 * @param {String} relation.collections Array of collections name. This or
 * `relation.collection` any one should be specified.
 * @param {String|Array} relation.field Name or location of the field in the
 * `state.selected` object that contains list of references.
 * @param {String} relation.parent Collection in which parent document exists.
 * @param {Object} api Specify your own api to perform db operations
 *
 * @returns {Function} that will take a module and apply the plugin.
 */
export default function(relation = {}, api = {}) {
  return function(module) {
    let { collections = [], collection, field, parent } = relation;
    const nestedPath = Array.isArray(parent) && parent[1];
    const getSelected = state =>
      nestedPath ? state.selected[nestedPath[1]] : state.selected;

    if (collection) collections.push(collection);

    const ucName = module.name.toUpperCase();
    const ADD_REF = `${ucName}_ADD_CHILD_REF`;
    const REMOVE_REF = `${ucName}_REMOVE_CHILD_REF`;
    module.addMutationTypes([ADD_REF, REMOVE_REF]);

    // No need to add these
    const GET_DONE = `${ucName}_GET_SELECTED_DONE`;
    const SUBMIT_INIT = `${ucName}_SUBMIT_INIT`;
    const SUBMIT_DONE = `${ucName}_SUBMIT_DONE`;

    const defaultApi = getDefaultApi(parent, field);
    const attach = api.attach || defaultApi.attach;
    const detach = api.detach || defaultApi.detach;
    const move = api.move || defaultApi.move;

    // Adds state required to maintain list references
    const state = {};
    collections.forEach(child => (state[child] = []));

    const getters = {
      attached: function(state) {
        const selected = getSelected(state);
        if (!selected || !Array.isArray(selected[field])) return [];

        return selected[field].map(ref => {
          const [collection, id] = ref.path.split('/');
          const data = state[collection].find(o => o.id === id);
          return {
            collection,
            id,
            ...data
          };
        });
      },
      detached: function(state, { attached }) {
        const selected = getSelected(state);
        if (!selected || !Array.isArray(selected[field])) return [];

        const detached = [];
        collections.forEach(collection => {
          state[collection].forEach(item => {
            const isAttached = attached.find(o => o.id === item.id);
            if (isAttached) return;

            detached.push({ collection, id: item.id, ...item });
          });
        });
        return detached;
      }
    };

    const mutations = {
      [ADD_REF](state, ref) {
        getSelected(state)[field].push(ref);
      },
      [REMOVE_REF](state, ref) {
        const index = findIndex(
          getSelected(state)[field],
          o => o.id === ref.id
        );
        removeByIndex(getSelected(state)[field], index);
      }
    };

    const actions = {
      async attachReference({ commit, state }, data) {
        commit(SUBMIT_INIT);
        try {
          const addedRef = await attach(state.selected.id, data.path);
          commit(ADD_REF, addedRef);
          commit(SUBMIT_DONE);
        } catch (error) {
          commit(SUBMIT_DONE);
          throw error;
        }
      },
      bindReferences: firestoreAction(
        async ({ bindFirestoreRef, unbindFirestoreRef }, data) => {
          if (!shouldBindReferences(data, field)) {
            collections.forEach(o => unbindFirestoreRef(o));
          }

          const ownerId = db
            .collection(nestedPath ? parent[0] : parent)
            .doc(data.id);
          await Promise.all(
            collections.map(collection => {
              const ref = db
                .collection(collection)
                .where('ownerId', '==', ownerId);
              return bindFirestoreRef(collection, ref, { maxRefDepth: 0 });
            })
          );
        }
      ),
      async detachReference({ commit, state }, data) {
        commit(SUBMIT_INIT);
        try {
          const removedRef = await detach(state.selected.id, data.path);
          commit(REMOVE_REF, removedRef);
          commit(SUBMIT_DONE);
        } catch (error) {
          commit(SUBMIT_DONE);
          throw error;
        }
      },
      async moveReference({ commit, state }, data) {
        commit(SUBMIT_INIT);
        try {
          const updatedData = await move(state.selected, data.from, data.to);
          commit(GET_DONE, updatedData);
          commit(SUBMIT_DONE);
        } catch (error) {
          commit(SUBMIT_DONE);
          throw error;
        }
      }
    };

    return module.extend({ state, getters, mutations, actions });
  };
}
